/* eslint-disable react/no-unused-prop-types, no-shadow, max-lines, react/no-multi-comp */
import React, { lazy, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import ErrorIcon from '@material-ui/icons/Error';

import { translate } from 'services/i18nService';
import { PAYMENT_TOKEN_AWARENESS_TRIGGER } from 'services/MessagingService/EventNames';
import getMessagingServiceInstance from 'services/MessagingService/MessagingService';
import SessionStorageService from 'services/SessionStorageService';
import ChatStore from 'stores/ChatStore';
import FanClubStore from 'stores/FanClubStore';
import FirebaseStore from 'stores/FirebaseStore';
import GamesStore from 'stores/GamesStore';
import ProfileStore from 'stores/ProfileStore';
import TippingStore from 'stores/TippingStore';
import UserStore from 'stores/UserStore';
import ChatMobileMessages from
  'pages/ProfileV2/ProfileV2Mobile/MobileBroadcast/MobileChatV2/ChatMobileMessages/ChatMobileMessages';
import TokenAwarenessDesktop from 'components/TokenAwarenessDesktop/TokenAwarenessDesktop';
import TipLeaderChat from 'components/TipLeaderChat/TipLeaderChat';
import PrivateChatBasicAnnouncement from 'components/PrivateChatBasicAnnouncement/PrivateChatBasicAnnouncement';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import { NOTIFICATION_TYPE } from 'enum/messages';
import { BROADCAST_STATUS } from 'enum/videoPlayer';
import { shouldShowTokenAwarenessOverlay } from 'utils/helpers';
import lockedImg from 'images/icons/locked.png';

import PublicChat from './PublicChat/PublicChat';
import {
  broadcasterAsViewer,
  getEncryptedUserName,
  getLovenseDataHandler,
  isAvailableToChangeLovense,
  setCookie,
} from './helpers';
import styles from './DesktopChatV2.css';


const RANDOM_NUMBER_RANGE = 100000000;

const RegistrationGoldModal = lazy(() => import('components/RegistrationGoldModal/RegistrationGoldModal'));

// eslint-disable-next-line complexity
const DesktopChatV2 = (props) => {
  const {
    embedProfileV2,
    handleTabsChange,
    isBroadcasterChat,
    isGroupShowChat,
    isPrivateUserChat,
    isRegisterGoldOverlayVisible,
    paymentActions: {
      showBuyTokensModal,
      toggleRegisterGoldOverlay,
    },
    recipient,
    roomName,
    user,
  } = props;

  const {
    isChatShown,
    privateMessages,
    privateUserChats,
    isPrivateMessageOpen,
    addMessage,
    initialMessagesSet,
    resetInitialMessageState,
    deleteLiveTouchNotif,
    groupShowMessagesAndNotifications,
    liveTouchMessageAdded,
    deletePrivateMessages,
    chatSettings: viewerChatSettings,
    chatSettingsPerformer,
    hasPrivateMessages,
    haveMessagesWithPerformer,
    resetTranslationSettings,
    setRegularFontGuest,
  } = ChatStore;

  const {
    isStreamPortrait,
    kiirooRoom,
    isLoggedInUserRoomModerator,
    webUsers,
    broadcastStartTime,
    kcid,
  } = FirebaseStore;

  const {
    broadcastStatus,
    fullscreen,
    performerDetails,
    profileName,
  } = ProfileStore;

  const {
    anyGameActive,
    activeGamesInfo,
    checkActiveGamesAndStartAutoNotifications,
  } = GamesStore;

  const {
    tippingInfo: { toptipper },
    performerTopTipper,
    performerTopTipperAmount,
  } = TippingStore;

  const { isFan } = FanClubStore;
  const { isBasicUser } = UserStore;

  const [isInputFocused, setIsInputFocused] = useState(false);
  const [gameDrawerOpen, setGameDrawerOpen] = useState(false);
  const [overlayShown, setOverlayShown] = useState(false);

  const chatSettings = isBroadcasterChat ? chatSettingsPerformer : viewerChatSettings;
  const { username, accessLevel } = user;
  const performerName = performerDetails?.username || '';
  const isGold = accessLevel >= ACCESS_LEVEL_NUM.GOLD;
  const isRoomModerator = isLoggedInUserRoomModerator(username);
  const TOKEN_AWARENESS_TIMEOUT = 120000;
  const messagingService = getMessagingServiceInstance();
  const wasUserInPrivateShow = Boolean(SessionStorageService.get('wasUserInPrivateShow'));
  const privateChatMessagesWithPerformer = privateUserChats?.[performerName]?.messages || [];
  const privateChatMessagesWithRecipient = privateUserChats[recipient]?.messages || [];
  const canPMBroadcaster =
    (isBasicUser && wasUserInPrivateShow) ||
    (isBasicUser && isRoomModerator) ||
    isGold ||
    isFan ||
    haveMessagesWithPerformer(performerName);
  const isBasicAnnouncementVisible =
    isPrivateMessageOpen &&
    !isGold &&
    !hasPrivateMessages &&
    broadcastStatus !== BROADCAST_STATUS.ACCEPTED_INSIDE_PS &&
    !isFan &&
    !recipient &&
    !chatSettings.disablePrivateChat &&
    !canPMBroadcaster;
  const isPrivateToShow = isPrivateMessageOpen &&
    !isGroupShowChat &&
    !recipient &&
    !chatSettings.disablePrivateChat &&
    canPMBroadcaster;
  const isBroadcasterAsViewer = broadcasterAsViewer(user, profileName);
  const toggleGameDrawer = useCallback(() => {
    setGameDrawerOpen(!gameDrawerOpen);
  }, [gameDrawerOpen]);

  const closeOverlay = useCallback(() => {
    setOverlayShown(false);
  }, []);

  const handlePromoOverlay = useCallback(() => {
    let timer = undefined;
    if (shouldShowTokenAwarenessOverlay(user, isBroadcasterChat)) {
      timer = setTimeout(() => {
        setOverlayShown(true);
        setCookie();
      }, TOKEN_AWARENESS_TIMEOUT);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [isBroadcasterChat, user]);

  const closeGoldRegisterModal = useCallback(() => {
    toggleRegisterGoldOverlay(false);
  }, [toggleRegisterGoldOverlay]);

  const initializeLovense = useCallback(() => {
    const { user } = UserStore;
    const { username: viewerUsername, accessHash: viewerAccessHash } = user;

    if (!viewerUsername) {
      return;
    }

    const queryParams = {
      username: viewerUsername,
      accessHash: viewerAccessHash,
    };

    const postParams = {
      roomName: performerName,
      username: viewerUsername,
    };

    getEncryptedUserName(queryParams, postParams);
  }, [performerName]);

  const getLovenseData = useCallback(() => {
    const { user } = UserStore;
    const { username: viewerUsername, accessHash: viewerAccessHash } = user;
    const queryParams = {
      username: viewerUsername,
      accessHash: viewerAccessHash,
    };

    getLovenseDataHandler(queryParams, kcid);
  }, [kcid]);

  useEffect(() => {
    resetInitialMessageState();
    if (!username && privateMessages.length > 0) {
      deletePrivateMessages();
    }
  }, [username, privateMessages, resetInitialMessageState, deletePrivateMessages]);

  useEffect(() => {
    if (activeGamesInfo.length > 0 && broadcastStartTime && initialMessagesSet) {
      checkActiveGamesAndStartAutoNotifications();
    }
  }, [broadcastStartTime, activeGamesInfo, initialMessagesSet, checkActiveGamesAndStartAutoNotifications]);

  useEffect(() => {
    const liveTouchMessage = {
      type: NOTIFICATION_TYPE.LIVE_TOUCH,
      messageKey: `chatMessages${Math.round(Math.random() * RANDOM_NUMBER_RANGE)}`,
      roomName,
    };

    if (kiirooRoom && !liveTouchMessageAdded) {
      addMessage(liveTouchMessage);
    } else if (!kiirooRoom && liveTouchMessageAdded) {
      deleteLiveTouchNotif();
    }
  }, [kiirooRoom, addMessage, roomName, deleteLiveTouchNotif, liveTouchMessageAdded]);

  useEffect(() => {
    if (!username) {
      resetTranslationSettings();
      setRegularFontGuest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  useEffect(() => {
    if (!anyGameActive && gameDrawerOpen) {
      toggleGameDrawer();
    }
  }, [anyGameActive, gameDrawerOpen, toggleGameDrawer]);

  useEffect(() => {
    handlePromoOverlay();
  }, [handlePromoOverlay]);

  useEffect(() => {
    const tokenAwarnessHandler = () => {
      setOverlayShown(true);
    };

    messagingService.on(PAYMENT_TOKEN_AWARENESS_TRIGGER, tokenAwarnessHandler);

    return () => {
      messagingService.off(PAYMENT_TOKEN_AWARENESS_TRIGGER, tokenAwarnessHandler);
    };
  }, [messagingService]);

  useEffect(() => {
    if (isAvailableToChangeLovense()) {
      initializeLovense();
    }

    return () => {
      if (isAvailableToChangeLovense()) {
        window.Lovense.destroy();
      }
    };
  }, [initializeLovense]);

  useEffect(() => {
    if (window.Lovense && kcid) {
      getLovenseData();
    }
  }, [getLovenseData, kcid]);

  const showPortraitStream = (isStreamPortrait || fullscreen) && broadcastStatus !== BROADCAST_STATUS.RECONNECTING;

  const chatClasses = classNames({
    [styles.mobileChatWrapperPortrait]: showPortraitStream,
    [styles.mobileChatWrapperLandscape]: !showPortraitStream,
    [styles.theatreModeBar]: !isChatShown,
    [styles.desktopVersion]: true,
    [styles.multiplePrivateChatsWrapper]: isPrivateUserChat,
  });
  const msgWrapperClasses = classNames({
    [styles.mobileMsgWrapperPortrait]: showPortraitStream,
    [styles.mobileMsgWrapperLandscape]: !showPortraitStream,
    [styles.multiplePrivateChatsWrap]: isPrivateUserChat,
  });

  const onEnablePrivateChatWithBC = useCallback(() => {
    const { user } = UserStore;
    const { username, accessHash } = user;
    const { toggleChatFilters } = ChatStore;

    if (username) {
      const filter = { disablePrivateChat: false };
      toggleChatFilters(filter, username, accessHash, profileName);
    }
  }, [profileName]);

  const enablePrivateChatWithBroadcasterElement = useMemo(() => {
    const enableButton = (
      <button
        className={styles.enablePrivateChat}
        onClick={onEnablePrivateChatWithBC}
      >
        {translate('v2profile.privateChat.button.enable')}
      </button>
    );

    return (
      <div>
        <div>
          {translate('v2profile.privateChat.withBroadcaster.isDisabled')}
        </div>
        <div>
          {translate('v2profile.privateChat.prompt.enable')}
          {enableButton}
        </div>
      </div>
    );
  }, [onEnablePrivateChatWithBC]);

  return (
    <div className={chatClasses}>
      {overlayShown && !isBroadcasterAsViewer &&
        <TokenAwarenessDesktop
          closeOverlay={closeOverlay}
          showBuyTokensModal={showBuyTokensModal}
        />
      }

      {!isBroadcasterChat && toptipper && !isPrivateMessageOpen && !isPrivateUserChat && (
        <TipLeaderChat
          roomName={roomName}
          showBuyTokensModal={showBuyTokensModal}
          topTipper={toptipper}
          user={user}
        />
      )}

      {isBroadcasterChat && performerTopTipper && !isPrivateUserChat && (
        <TipLeaderChat
          isBroadcasterChat={true}
          roomName={roomName}
          showBuyTokensModal={false}
          topTipper={performerTopTipper}
          topTipperAmount={performerTopTipperAmount}
          user={user}
        />
      )}

      {isBasicAnnouncementVisible ? (
        <PrivateChatBasicAnnouncement
          toggleRegisterGoldOverlay={toggleRegisterGoldOverlay}
          user={user}
        />
      ) : (
        isPrivateMessageOpen &&
        !recipient &&
        !isGroupShowChat &&
        (chatSettings.disablePrivateChat ? (
          <>
            <h5 className={styles.title}>
              {translate('v2profile.chat.private.conversation')}
            </h5>
            <div className={styles.chatDisabled}>
              <ErrorIcon className={styles.chatDisabledIcon} />
              {enablePrivateChatWithBroadcasterElement}
            </div>
          </>
        ) : (
          <div className={styles.performerChatTip}>
            <img alt="" src={lockedImg} />
            <span>
              {translate('v2profile.private.conversation.tip', { 0: performerName })}
            </span>
          </div>
        ))
      )}

      {isChatShown && (
        <div className={msgWrapperClasses}>
          {(isPrivateUserChat || recipient) && (
            <ChatMobileMessages
              embedProfileV2={embedProfileV2}
              handleTabsChange={handleTabsChange}
              id="CHAT1"
              isBroadcasterChat={isBroadcasterChat}
              isDesktop={true}
              isInputFocused={isInputFocused}
              isPrivateUserChat={isPrivateUserChat}
              messages={privateChatMessagesWithRecipient}
              recipient={recipient}
              roomName={roomName}
              rosterUsers={webUsers}
              setIsInputFocused={setIsInputFocused}
              showBuyTokensModal={showBuyTokensModal}
              toggleGameDrawer={toggleGameDrawer}
            />
          )}
          <PublicChat
            embedProfileV2={embedProfileV2}
            isBroadcasterChat={isBroadcasterChat}
            isGroupShowChat={isGroupShowChat}
            isInputFocused={isInputFocused}
            isPrivateUserChat={isPrivateUserChat}
            recipient={recipient}
            roomName={roomName}
            setIsInputFocused={setIsInputFocused}
            showBuyTokensModal={showBuyTokensModal}
            toggleGameDrawer={toggleGameDrawer}
            onTabsChange={handleTabsChange}
          />
          {isPrivateToShow && (
            <ChatMobileMessages
              embedProfileV2={embedProfileV2}
              handleTabsChange={handleTabsChange}
              hideInputInPrivateChat={isBasicAnnouncementVisible}
              id="CHAT3"
              isBroadcasterChat={isBroadcasterChat}
              isDesktop={true}
              isInputFocused={isInputFocused}
              messages={privateChatMessagesWithPerformer}
              roomName={roomName}
              rosterUsers={webUsers}
              setIsInputFocused={setIsInputFocused}
              showBuyTokensModal={showBuyTokensModal}
              toggleGameDrawer={toggleGameDrawer}
            />
          )}
          {isGroupShowChat && (
            <ChatMobileMessages
              embedProfileV2={embedProfileV2}
              handleTabsChange={handleTabsChange}
              id="CHAT4"
              isBroadcasterChat={isBroadcasterChat}
              isDesktop={true}
              isGroupShowChat={true}
              isInputFocused={isInputFocused}
              messages={groupShowMessagesAndNotifications}
              roomName={roomName}
              rosterUsers={webUsers}
              setIsInputFocused={setIsInputFocused}
              showBuyTokensModal={showBuyTokensModal}
              showPortraitStream={showPortraitStream}
              toggleGameDrawer={toggleGameDrawer}
            />
          )}
        </div>
      )}
      {isRegisterGoldOverlayVisible && (
        <RegistrationGoldModal
          history={history}
          showRegistrationGoldModal={isRegisterGoldOverlayVisible}
          onClose={closeGoldRegisterModal}
        />
      )}
    </div>
  );
};

DesktopChatV2.propTypes = {
  paymentActions: PropTypes.object.isRequired,
  embedProfileV2: PropTypes.bool,
  handleTabsChange: PropTypes.func,
  history: PropTypes.object,
  isBroadcasterChat: PropTypes.bool,
  isGroupShowChat: PropTypes.bool,
  isPrivateUserChat: PropTypes.bool,
  isRegisterGoldOverlayVisible: PropTypes.bool,
  recipient: PropTypes.string,
  roomName: PropTypes.string,
  user: PropTypes.object,
};

DesktopChatV2.defaultProps = {
  embedProfileV2: false,
  handleTabsChange: undefined,
  history: undefined,
  isBroadcasterChat: false,
  isGroupShowChat: false,
  isPrivateUserChat: false,
  isRegisterGoldOverlayVisible: false,
  recipient: undefined,
  roomName: undefined,
  user: undefined,
};

export default observer(DesktopChatV2);
